import { ReactNode, useContext, useEffect, useState } from 'react';
import { X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { EmbedContext } from './EmbedProvider';
import { assetUrl } from '../../common/helpers';
import LanguageSelector from '../../common/Common/LanguageSelector';
import SC from '../../common/UI/SC';
import UI from '../../common/UI';

const OpenEmbedButton = () => {
  const { t } = useTranslation();

  const { setOpen } = useContext(EmbedContext);

  const openEmbed = () => {
    setOpen(true);
  };

  return (
    <UI.FadeIn>
      <UI.AButton
        onClick={() => openEmbed()}
        sc={{ brand: 'secondary' }}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          paddingTop: 6.5,
          paddingBottom: 6.5,
          border: '2px solid rgba(0, 0, 0, 0.05)',
          lineHeight: '20px',
        }}
        role="button"
      >
        <img
          src={assetUrl('images/logo-symbol-white.svg')}
          alt="Atleta"
          style={{
            width: 14, marginTop: 2, marginBottom: -2, marginRight: 4, opacity: 0.4,
          }}
        />
        {' '}
        {t('register')}
      </UI.AButton>
    </UI.FadeIn>
  );
};

const TopBarContainer = styled(UI.Div)<{ embedded: boolean; }>`
  ${({ embedded, theme }) => css`
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    ${embedded && css`
      background: ${theme.mix(theme.colors.secondary[300], theme.colors.gray[100], 0.8)};

      a {
        color: ${theme.colors.gray[600]};

        &:hover {
          color: ${theme.colors.gray[800]};
        }
      }
    `}

    > div {
      max-width: ${theme.breakpoints.md - 1.5 * theme.gutter}px;
      margin: 0 auto;
      padding: 0 ${theme.gutter * 0.5}px;
      height: 48px;
    }

    ${embedded && css`
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 101;
    `}

    ${embedded && css`
      @media ${theme.devices.md} {
        top: ${theme.gutter}px;
        right: ${theme.gutter}px;
        left: ${theme.gutter}px;
        border-top-left-radius: ${theme.borderRadiuses['2xl']}px;
        border-top-right-radius: ${theme.borderRadiuses['2xl']}px;

        > div {
          padding: 0 ${theme.gutter * 1.25}px;
        }
      }
    `}
  `}
`;

interface TopBarProps {
  embedded: boolean;
  closeEmbed?: () => void;
}

const TopBar = ({ embedded, closeEmbed }: TopBarProps) => (
  <TopBarContainer embedded={embedded}>
    <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <LanguageSelector blank alignH="left" alignV="bottom" />
      {closeEmbed && (
        <UI.A onClick={() => closeEmbed()} sc={{ fontSize: 4 }}>
          <UI.Icon>
            <X />
          </UI.Icon>
        </UI.A>
      )}
    </UI.FlexContainer>
  </TopBarContainer>
);

interface OuterContainerProps {
  open?: boolean;
}

const OuterContainer = styled(UI.Div)<SC<OuterContainerProps>>`
  ${({ sc: { open } = {}, theme }) => css`
    height: 40px;
    max-width: ${theme.breakpoints.md}px;
    max-height: 100%;
    position: relative;
    transition: height 0.2s ease-in;

    ${open && css`
      width: 100%;
      height: 100%;
      position: absolute;
      bottom: 0;
      right: 0;

      @media ${theme.devices.md} {
        padding: ${theme.gutter}px;
      }
    `}
  `}
`;

const InnerContainer = styled(UI.Div)`
  ${({ theme }) => css`
    min-width: 320px;
    padding-top: ${theme.gutter * 2}px;
    min-height: 100%;

    #PageContainer {
      background: ${theme.colors.gray[100]};
      background: linear-gradient(180deg, ${theme.mix(theme.colors.secondary[300], theme.colors.gray[100], 0.8)} 0px, ${theme.colors.gray[100]} 80px);
    }

    @media ${theme.devices.md} {
      width: 100%;
      height: 100%;
      border-radius: ${theme.borderRadiuses['2xl']}px;
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1);
      overflow: hidden;

      #PageContainer {
        width: 100%;
        height: 100%;
        overflow-y: auto;
        position: relative;
      }
    }
  `}
`;

interface EmbedContainerProps {
  showRegisterButton?: boolean;
  children?: ReactNode;
}

const EmbedContainer = ({ showRegisterButton, children }: EmbedContainerProps) => {
  const { open, setOpen } = useContext(EmbedContext);
  const [showingWindow, setShowingWindow] = useState(false);
  const [showingContent, setShowingContent] = useState(false);
  const [showingOpenButton, setShowingOpenButton] = useState(true);

  /**
   * For a smooth experience we first slide in the window, then fade in the content.
   * When the window is closed, show the button after a while.
   */
  useEffect(() => {
    setShowingContent(false);
    setShowingWindow(false);

    if (open) {
      setShowingOpenButton(false);

      const windowTimer = setTimeout(() => {
        setShowingWindow(true);
      }, 50);

      const contentTimer = setTimeout(() => {
        setShowingContent(true);
      }, 200);

      return () => {
        clearTimeout(windowTimer);
        clearTimeout(contentTimer);
      };
    }

    const buttonTimer = setTimeout(() => {
      setShowingOpenButton(true);
    }, 500);

    return () => clearTimeout(buttonTimer);
  }, [open]);

  const closeEmbed = () => {
    window.scrollTo(0, 0);
    setOpen(false);
  };

  return (
    <OuterContainer sc={{ open: showingWindow }}>
      {showRegisterButton && showingOpenButton && (
        <OpenEmbedButton />
      )}
      {showingWindow && (
        <>
          <TopBar embedded closeEmbed={closeEmbed} />
          <InnerContainer>
            <div id="PageContainer">
              {showingContent && children}
            </div>
          </InnerContainer>
        </>
      )}
    </OuterContainer>
  );
};

export interface WidgetContainerProps {
  showRegisterButton?: boolean;
  children?: ReactNode;
}

const WidgetContainer = ({ showRegisterButton, children }: WidgetContainerProps) => {
  const { embedded } = useContext(EmbedContext);

  return (
    <>
      {embedded && (
        <EmbedContainer showRegisterButton={showRegisterButton}>
          {children}
        </EmbedContainer>
      )}
      {!embedded && (
        <>
          <TopBar embedded={false} />
          {children}
        </>
      )}
    </>
  );
};

export default WidgetContainer;
